import React, { useState, createContext, useEffect, useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import funcionalidades from "./AuthContext.funcionalidades";
import { history } from "../history";
import { selectInputCacheActions } from "../redux/selectInputCache";
import { apiRoutes } from "../hooks/apiClient";
import {
  callSsoSignOut,
  clearSsoSessionMarker,
  fetchSsoSessionActive,
  getPollIntervalMs,
  isSsoSessionMarked,
  markSsoSessionActive,
} from "../utils/ssoLogoutGlobal";

const AuthContext = createContext({
  isAuthenticated: false,
  unreadNotifications: [],
  login: () => {},
});

const AuthProvider = ({ children }) => {
  const [authenticated, setAuthenticated] = useState(false);
  const [user, setUser] = useState({});
  const [unreadNotifications, setUnreadNotifications] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const dispatch = useDispatch();

  // Tentar carregar dados de login da sessão
  useEffect(() => {
    const userJson = localStorage.getItem("user");
    const tokenJson = localStorage.getItem("authToken");

    if (userJson && tokenJson) {
      const parsedUser = JSON.parse(userJson);
      setUser(parsedUser);
      setPermissions(parsedUser.permissoes);
      setAuthenticated(true);

      // Mantém o comportamento de logout global mesmo após F5/reabertura da SPA.
      if (!isSsoSessionMarked() && localStorage.getItem("loginType") === "sso") {
        markSsoSessionActive();
      }
    }
  }, []);

  const handleLogin = useCallback(
    (signinData, options = {}) => {
      localStorage.setItem("authToken", JSON.stringify(signinData.result.token));
      localStorage.setItem("user", JSON.stringify(signinData.result.user));

      if (options.sso) {
        markSsoSessionActive();
        localStorage.setItem("loginType", "sso");
      }

      setUser(signinData.result.user);
      setAuthenticated(true);
      setPermissions(signinData.result.user.permissoes);
      dispatch(selectInputCacheActions.reset());
      window.location.href = "/app/home";
    },
    [setUser, setAuthenticated, setPermissions, dispatch],
  );

  const handleLogout = useCallback(
    async (event) => {
      if (event?.preventDefault) event.preventDefault();
      if (event?.stopPropagation) event.stopPropagation();

      await callSsoSignOut(apiRoutes.auth.autenticacao.signOut.url);

      clearSsoSessionMarker();
      setAuthenticated(false);
      localStorage.clear();
      dispatch(selectInputCacheActions.reset());
      history.push("/");
    },
    [setAuthenticated, dispatch],
  );

  useEffect(() => {
    if (!authenticated || !isSsoSessionMarked()) return;

    let cancelled = false;

    const checkCentralSession = async () => {
      const active = await fetchSsoSessionActive(apiRoutes.auth.autenticacao.sessionStatus.url);
      if (!active && !cancelled) {
        handleLogout();
      }
    };

    checkCentralSession();
    const interval = setInterval(checkCentralSession, getPollIntervalMs());

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [authenticated, handleLogout]);

  const isAllowed = useCallback(
    (permissionRequired) => {
      try {
        let currentPermissions = permissions;

        if (!currentPermissions || !currentPermissions.length) {
          const localStorageUser = JSON.parse(localStorage.getItem("user"));
          currentPermissions = localStorageUser?.permissoes || [];
        }

        return currentPermissions.filter((x) => x.nomeFuncionalidade.includes(permissionRequired)).length >= 1;
      } catch (e) {
        console.error(e);
        return false;
      }
    },
    [permissions],
  );

  const recuperarSenhaSigat = useCallback(async () => {
    try {
      const response = await fetch(apiRoutes.auth.autenticacao.sessionPasswords.url, {
        method: apiRoutes.auth.autenticacao.sessionPasswords.method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.result || "Erro ao recuperar senha do SIGAT");
      }

      const data = await response.json();
      return data.result?.ora || data.ora || "";
    } catch (error) {
      console.error("Erro ao recuperar senha:", error);
      toast.error(error.message || "Não foi possível recuperar a senha do SIGAT");
      throw error;
    }
  }, []);

  const contextValue = useMemo(
    () => ({
      isAuthenticated: authenticated,
      user,
      unreadNotifications,
      setUnreadNotifications,
      isAllowed,
      handleLogin,
      handleLogout,
      recuperarSenhaSigat,
    }),
    [authenticated, handleLogin, handleLogout, isAllowed, unreadNotifications, user, recuperarSenhaSigat],
  );
  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export { AuthContext, AuthProvider, funcionalidades };
